import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSystemSettingsActions, useSystemSettingsState } from "@/store";
import {
  ANALYTICS_ON,
  ANALYTICS_OFF,
  readAnalyticsOverride,
} from "@/Service/AnalyticsToggle";
import { decideAnalyticsEnabled } from "@/observability/analytics/decideAnalyticsEnabled";

// Env-wide PostHog kill-switch (2026-08-06). Backed by the server-side
// system setting so an admin flip propagates to every user on this
// environment. The bootstrap layer reads a localStorage cache of the last
// server value on next reload — this UI writes the cache via the Redux
// thunk, and prompts a reload if the effective decision changes.
const environment = process.env.REACT_APP_POSTHOG_ENV || "unknown";

export const AnalyticsToggleSection = () => {
  const {
    analyticsEnabled,
    analyticsFetch,
    analyticsUpdate,
  } = useSystemSettingsState();
  const { fetchAnalyticsEnabled, updateAnalyticsEnabled } = useSystemSettingsActions();

  useEffect(() => {
    fetchAnalyticsEnabled();
  }, [fetchAnalyticsEnabled]);

  // Snapshot the personal override + the initial live provider ONCE on mount
  // so the "reload required" indicator reflects whether flipping the switch
  // would change what the CURRENTLY-LOADED PostHog session is doing. The
  // provider name is stashed on window by AnalyticsService.setProvider.
  const [mountedLiveProvider] = useState(() =>
    typeof window !== "undefined"
      ? window.__clipn_analytics_provider_name || null
      : null
  );
  const personalOverride = readAnalyticsOverride();
  const liveProviderLabel =
    mountedLiveProvider === "posthog" ? "PostHog (recording)"
      : mountedLiveProvider === "console" ? "Console (dev debug)"
        : mountedLiveProvider === "noop" ? "Disabled"
          : "unknown";

  const isUpdating = analyticsUpdate.status === "loading";
  const isFetching = analyticsFetch.status === "loading";

  const effectiveEnabled = useMemo(
    () => decideAnalyticsEnabled(environment, personalOverride, analyticsEnabled),
    [personalOverride, analyticsEnabled]
  );

  const needsReload =
    mountedLiveProvider !== null &&
    ((effectiveEnabled && mountedLiveProvider !== "posthog") ||
      (!effectiveEnabled && mountedLiveProvider === "posthog"));

  const handleToggle = (_e, next) => {
    updateAnalyticsEnabled(next ? ANALYTICS_ON : ANALYTICS_OFF);
  };

  const statusChip =
    analyticsEnabled === null
      ? `default for ${environment}`
      : `env-wide: ${analyticsEnabled}`;

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Analytics & Session Replay
          </Typography>
          <Tooltip
            arrow
            title={
              <>
                PostHog events + session recordings. Env-wide toggle: flipping
                this here affects every user on <b>{environment}</b> after
                their next reload. Personal browser overrides (set via
                DevTools on <code>clipn:analytics-override</code>) still win
                over the env-wide value.
              </>
            }
          >
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Stack>
        <Switch
          checked={effectiveEnabled}
          onChange={handleToggle}
          disabled={isUpdating || isFetching}
          data-testid="analytics-toggle"
        />
      </Stack>

      <Typography variant="caption" color="text.secondary">
        {environment} · {statusChip} · currently loaded: {liveProviderLabel}
      </Typography>

      {analyticsFetch.status === "failed" && (
        <Alert severity="error" variant="outlined">
          Couldn't load the current setting from the server. Toggle reflects
          the last cached value.
        </Alert>
      )}

      {needsReload && (
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          Change takes effect after reloading this tab.
        </Alert>
      )}
    </Stack>
  );
};

export default AnalyticsToggleSection;
